import { Paper, Tabs, type TabsTabProps } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router";
import { useConfiguratorComponents } from "../../hooks/useConfiguratorComponents";
import { useProject } from "../../hooks/useProject";
import { useSiteCategories } from "../../hooks/useSiteCategories";
import { useToast } from "../../mantine/notifications/useToast";
import type {
  ExtendedUser,
  Person,
  Site,
  SiteCategoriesResponse
} from "../../utils/backend-types";
import { ObjectName } from "../../utils/enums";
import { PageContent } from "../BuildingBlocks/Layout/PageContent/PageContent";
import { PageHeader } from "../BuildingBlocks/Layout/PageHeader/PageHeader";
import { LoadOrError } from "../LoadOrError/LoadOrError";
import { useShouldShowStaffView } from "../StaffViewToggle/useShouldShowStaffView";
import { CompaniesPage } from "./Companies/CompaniesPage";
import { DeleteSiteModal } from "./DeleteSiteModal/DeleteSiteModal";
import { DeleteSiteSetupProcessModal } from "./DeleteSiteModal/DeleteSiteSetupProcessModal";
import { GeneratorsPage } from "./Generators/GeneratorsPage";
import { useProjectOverviewSites } from "./hooks/useProjectOverviewSites";
import type { SiteSetupProcess } from "./hooks/useSiteSetupProcesses";
import { useSiteSetupProcesses } from "./hooks/useSiteSetupProcesses";
import { ProjectViewTabs } from "./MsmProjectView.constants";
import { RightsPage } from "./Rights/RightsPage";
import { SettingsPage } from "./Settings/SettingsPage";
import { SitesPage } from "./Sites/SitesPage";

type TabProps = TabsTabProps & { label: string };

export { ProjectViewTabs } from "./MsmProjectView.constants";

const getTabs = (isStaff: boolean) => {
  const TABS: ReadonlyArray<TabProps> = [
    {
      value: ProjectViewTabs.Sites,
      label: "Liegenschaften"
    },
    {
      value: ProjectViewTabs.Companies,
      label: "Unternehmen"
    },
    {
      value: ProjectViewTabs.Generators,
      label: "Erzeuger"
    },
    {
      value: ProjectViewTabs.Rights,
      label: "Rechte"
    },
    {
      value: ProjectViewTabs.Settings,
      label: "Einstellungen"
    },
    // if isStaff add service tab
    ...(isStaff
      ? [
          {
            value: ProjectViewTabs.Service,
            label: "Service"
          }
        ]
      : [])
  ];

  return TABS;
};

export const DEFAULT_SUBPAGE = ProjectViewTabs.Sites;

interface MsmProjectViewProps {
  initialProjectSites: ReadonlyArray<Site>;
  initialSiteSetupProcesses: ReadonlyArray<SiteSetupProcess>;
  projectManagers: Array<ExtendedUser>;
  projectCompanies: Array<Person>;
  projectPage: string;
  projectId: string;
  projectName: string;
  variantId: number;
  siteCategories: Array<SiteCategoriesResponse>;
}

function MsmProjectView({
  initialProjectSites,
  initialSiteSetupProcesses,
  projectManagers,
  projectCompanies,
  projectPage,
  projectId,
  projectName,
  variantId,
  siteCategories
}: MsmProjectViewProps) {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { shouldShowStaffView: isStaff } = useShouldShowStaffView();
  const queryClient = useQueryClient();
  const [siteToDelete, setSiteToDelete] = useState<Site | null>(null);
  const [siteSetupProcessToDelete, setSiteSetupProcessToDelete] =
    useState<SiteSetupProcess | null>(null);

  function handleShareProject(newManager: ExtendedUser) {
    if (!projectManagers.find((manager) => manager.id === newManager.id)) {
      showToast({
        type: "success",
        message: "Das Projekt wurde erfolgreich geteilt."
      });
    } else {
      showToast({
        type: "info",
        message: "Das Projekt wurde schon geteilt."
      });
    }
  }

  function handleSiteDeleted() {
    invalidateSites();
    setSiteToDelete(null);
  }
  function handleSiteSetupProcessDeleted() {
    invalidateSiteSetupProcesses();
    setSiteSetupProcessToDelete(null);
  }

  function invalidateSites() {
    queryClient.invalidateQueries({
      queryKey: ["project-overview-sites", { variantId }]
    });
  }

  function invalidateSiteSetupProcesses() {
    queryClient.invalidateQueries({
      queryKey: ["project-site-setup-processes-sites", { variantId }]
    });
  }

  const activeTab = projectPage;

  return (
    <PageContent className="MsmProjectView">
      <PageHeader title={projectName} />
      <Paper>
        <Tabs value={activeTab} onChange={(newTab) => navigate(`../${newTab}`)}>
          <Tabs.List>
            {getTabs(isStaff).map((tab) => (
              <Tabs.Tab component="a" key={tab.value} value={tab.value}>
                {tab.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
          <Tabs.Panel value={ProjectViewTabs.Sites}>
            {activeTab === ProjectViewTabs.Sites && (
              <SitesPage
                projectId={projectId}
                siteCategories={siteCategories}
                sites={initialProjectSites}
                siteSetupProcesses={initialSiteSetupProcesses}
                variantId={variantId}
                onDeleteSite={setSiteToDelete}
                onDeleteSiteSetupProcess={setSiteSetupProcessToDelete}
                onSiteCreated={() => {
                  invalidateSites();
                  invalidateSiteSetupProcesses();
                }}
                onSiteEdited={invalidateSites}
              />
            )}
          </Tabs.Panel>
          <Tabs.Panel value={ProjectViewTabs.Companies}>
            {activeTab === ProjectViewTabs.Companies && (
              <CompaniesPage
                companies={projectCompanies}
                variantId={variantId}
              />
            )}
          </Tabs.Panel>
          <Tabs.Panel value={ProjectViewTabs.Generators}>
            {activeTab === ProjectViewTabs.Generators && (
              <GeneratorsPage variantId={variantId} />
            )}
          </Tabs.Panel>
          <Tabs.Panel value={ProjectViewTabs.Rights}>
            {activeTab === ProjectViewTabs.Rights && (
              <RightsPage
                projectId={projectId}
                projectManagers={projectManagers}
                projectName={projectName}
                onShare={handleShareProject}
              />
            )}
          </Tabs.Panel>
          <Tabs.Panel value={ProjectViewTabs.Settings}>
            {activeTab === ProjectViewTabs.Settings && (
              <SettingsPage
                projectId={projectId}
                projectSites={initialProjectSites}
                siteCategories={siteCategories}
                variantId={variantId}
              />
            )}
          </Tabs.Panel>
          <Tabs.Panel value={ProjectViewTabs.Service}>
            {activeTab === ProjectViewTabs.Service && (
              <Link
                to={`/manager/projekte/${projectId}/vermarktung?variantId=${variantId}`}
              >
                Vermarktungsverträge für dieses Projekt
              </Link>
            )}
          </Tabs.Panel>
        </Tabs>
      </Paper>
      {siteToDelete && (
        <DeleteSiteModal
          site={siteToDelete}
          toggleModal={() => setSiteToDelete(null)}
          onSuccess={handleSiteDeleted}
        />
      )}
      {siteSetupProcessToDelete && (
        <DeleteSiteSetupProcessModal
          setupProcess={siteSetupProcessToDelete}
          toggleModal={() => setSiteSetupProcessToDelete(null)}
          onSuccess={handleSiteSetupProcessDeleted}
        />
      )}
    </PageContent>
  );
}

interface MsmProjectViewDataWrapperProps {
  projectId: string;
}

function MsmProjectViewDataWrapper({
  projectId
}: MsmProjectViewDataWrapperProps) {
  const { projectPage } = useParams();
  const {
    isLoading: isProjectLoading,
    error: projectError,
    project
  } = useProject(projectId);
  const {
    siteCategories,
    isLoading: isSiteCategoriesLoading,
    error: siteCategoriesError
  } = useSiteCategories(projectId ?? null);
  const variant = project ? project.variants[0] : undefined;
  const {
    sites,
    isLoading: isSitesLoading,
    error: sitesError
  } = useProjectOverviewSites(variant?.id);

  const {
    siteSetupProcesses,
    isLoading: isSiteSetupProcessesLoading,
    error: siteSetupProcessesError
  } = useSiteSetupProcesses(variant?.id);

  const {
    data: projectCompanies,
    isLoading: isProjectCompaniesLoading,
    error: projectCompaniesError
  } = useConfiguratorComponents<Person>(ObjectName.Person, variant?.id);

  if (!projectPage) {
    return <Navigate to={DEFAULT_SUBPAGE} />;
  }

  return (
    <LoadOrError
      error={
        projectError ||
        siteCategoriesError ||
        sitesError ||
        projectCompaniesError ||
        siteSetupProcessesError
      }
      loading={
        isProjectLoading ||
        isSiteCategoriesLoading ||
        isSitesLoading ||
        isProjectCompaniesLoading ||
        isSiteSetupProcessesLoading
      }
    >
      {project && variant && siteCategories && projectCompanies && (
        <MsmProjectView
          initialProjectSites={sites}
          initialSiteSetupProcesses={siteSetupProcesses}
          projectCompanies={projectCompanies}
          projectId={project.id}
          projectManagers={project.managers}
          projectName={project.name}
          projectPage={projectPage}
          siteCategories={siteCategories}
          variantId={variant.id}
        />
      )}
    </LoadOrError>
  );
}

export { MsmProjectViewDataWrapper as MsmProjectView };
